/* eslint-disable max-statements */
import { Button, Group, Modal, Stack, Switch } from '@mantine/core';
import { get, merge } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';

import { Declaration, DeclarationDocumentsEnum, DeclarationHighValueGroupType, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { Select } from '@components';
import { MutateDeclarationDocumentOperation } from '@hooks/declaration';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';
import { trimEdges } from '@utils';
import { DeclarationDocumentFormFactory } from './DeclarationDocumentFactory';
import { convertDotNotationToObject } from './DeclarationDocumentFactoryUtils';
import { buildDeclarationDocumentSchema, FormDocumentsDeclarationType, FormDocumentsInputType } from './DeclarationDocumentFormSchema';
import { shouldTrimField, validateDocumentFields } from './DeclarationDocumentValidation';

export interface DeclarationDetailsDocDetailsProps {
  type: DeclarationDocumentsEnum;
  dataSource: any;
  dataSourceIndex?: number;
}

interface DeclarationDocumentViewProps {
  opened: boolean;
  onHide: () => void;
  docDetails?: DeclarationDetailsDocDetailsProps;
  includeDocumentTypes?: DeclarationDocumentsEnum[];
  excludeDocumentTypes: DeclarationDocumentsEnum[];
  onSave: (savedDocument: {
    id: Record<string, any>;
    operation: MutateDeclarationDocumentOperation.CREATE | MutateDeclarationDocumentOperation.UPDATE;
    type: DeclarationDocumentsEnum;
    data: any;
  }) => void;
  editDisabled: boolean;
  defaultHeaderValue?: boolean;
  headerDisabled?: boolean;
  isHighValue?: boolean;
  declaration?: Declaration;
  isGoodsItem?: boolean;
}

export const documentsLabels = {
  [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS]: 'Previous document',
  [DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS]: 'Supporting document',
  [DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS]: 'Transport document',
  [DeclarationDocumentsEnum.ADDITIONAL_REFERENCE]: 'Additional reference',
  [DeclarationDocumentsEnum.ADDITIONAL_INFORMATION]: 'Additional information',
  [DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS]: 'Guarantee document',
  [DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT]: 'Warehouse document',
  [DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS]: 'Authorisation document',
  [DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS]: 'Additions and deductions',
  [DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE]: 'Additional fiscal reference',
};

export const DeclarationDocumentView: React.FC<DeclarationDocumentViewProps> = ({
  defaultHeaderValue,
  includeDocumentTypes,
  excludeDocumentTypes,
  docDetails,
  onSave,
  onHide,
  isHighValue,
  headerDisabled,
  opened,
  editDisabled,
  declaration,
  isGoodsItem,
}) => {
  const { data: settings } = useSettings();
  const schema = buildDeclarationDocumentSchema(
    settings.country,
    isHighValue ? (declaration?.generalInfo?.group ? declaration.generalInfo.group : DeclarationHighValueGroupType.H1) : undefined,
    isGoodsItem,
    declaration?.generalInfo?.isExport,
  );
  const context = !docDetails ? 'CREATE' : 'UPDATE';

  const documentsTypesList = useRef(
    Object.keys(documentsLabels)
      .filter(
        (key) =>
          includeDocumentTypes?.includes(key as DeclarationDocumentsEnum) ||
          (excludeDocumentTypes && !excludeDocumentTypes?.includes(key as DeclarationDocumentsEnum)) ||
          (!includeDocumentTypes && !excludeDocumentTypes),
      )
      .map((key) => ({ value: key, label: documentsLabels[key] })),
  );

  const buildInitialFieldValues = (type?: DeclarationDocumentsEnum, data?: any) => {
    if (!type) return {};

    const formSchema = schema[type];
    if (!formSchema?.fields) return {};

    const initialData = { header: data?.header ?? false };

    const processFieldValue = {
      [FormDocumentsInputType.NUMBER]: (value) => (value === null || isNaN(Number(value)) ? undefined : Number(value)),
      [FormDocumentsInputType.DATE]: (value) => {
        const dateValue = value?.split(' ')[0];
        return dateValue ? moment(dateValue, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : undefined;
      },
      default: (value) => value,
    };

    Object.entries(formSchema.fields).forEach(([fieldPath, fieldConfig]) => {
      const rawValue = get(data, fieldPath);
      const processor = processFieldValue[fieldConfig.type] || processFieldValue.default;
      const processedValue = processor(rawValue);

      Object.assign(initialData, merge({}, initialData, convertDotNotationToObject(fieldPath, processedValue)));
    });

    return initialData;
  };

  const form = useFormWithNameInputProp({
    initialValues: {
      documentType: docDetails?.type || null,
      operation: docDetails?.dataSource?.operation || undefined,
      header: docDetails?.dataSource?.header ?? (typeof defaultHeaderValue === 'boolean' ? defaultHeaderValue : true),
      type: '', // this field is added just for type, it is built from buildInitialFieldValues
      ...buildInitialFieldValues(docDetails?.type, docDetails?.dataSource),
    },
    validate: (values) => validateDocumentFields(schema, isHighValue, values),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (form.values.documentType && context === 'CREATE') {
      const currentSchema = schema[form.values.documentType as DeclarationDocumentsEnum];
      if (currentSchema?.fields && !docDetails?.dataSource?.data) {
        const newValues = buildInitialFieldValues(form.values.documentType as DeclarationDocumentsEnum, {
          header: typeof defaultHeaderValue === 'boolean' ? defaultHeaderValue : true,
        });
        form.setValues({
          ...form.values,
          ...newValues,
        });
      }
    }

    if (form.values.documentType === DeclarationDocumentsEnum.ADDITIONAL_REFERENCE && !headerDisabled && !docDetails) {
      form.setFieldValue('header', false);
    }

    if (form.values.documentType === DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS) {
      form.setFieldValue('header', true);
    }
  }, [form.values.documentType]);

  useEffect(() => {
    if (!form.values.documentType && context === 'CREATE' && documentsTypesList.current?.length === 1) {
      const type = documentsTypesList.current[0].value;
      const currentSchema = schema[type as DeclarationDocumentsEnum];
      if (currentSchema?.fields && !docDetails?.dataSource?.data) {
        const newValues = buildInitialFieldValues(type as DeclarationDocumentsEnum, {
          header: typeof defaultHeaderValue === 'boolean' ? defaultHeaderValue : true,
        });
        form.setValues({
          documentType: type as DeclarationDocumentsEnum,
          ...newValues,
        });
      }
    }
  }, [documentsTypesList.current, form.values.documentType, context]);

  const isHeaderDisabled = () => {
    if (
      headerDisabled &&
      form.values.documentType === DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS &&
      (['N820', 'N821', 'N822', 'N705', 'N337'].includes(form.values.type) || form.values.type === 'IM')
    ) {
      return false;
    }

    return headerDisabled || !isHighValue || [DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS, null].includes(form.values.documentType);
  };

  const onSubmit = (values) => {
    if (!values.documentType) return;

    const currentSchema = schema[values.documentType as DeclarationDocumentsEnum];
    if (!currentSchema?.fields) return;

    const relevantFieldPaths = Object.keys(currentSchema.fields).filter((path) => {
      const field = currentSchema.fields[path];
      const fieldDeclarationType = field.declarationType;

      return (
        fieldDeclarationType === (isHighValue ? FormDocumentsDeclarationType.HIGH_VALUES : FormDocumentsDeclarationType.LOW_VALUES) ||
        [undefined, FormDocumentsDeclarationType.ALL].includes(fieldDeclarationType)
      );
    });

    const fieldData = relevantFieldPaths.reduce((acc, fieldPath) => {
      const rawValue = get(values, fieldPath);
      const value = shouldTrimField(currentSchema.fields[fieldPath]) ? trimEdges(rawValue) : rawValue;

      return merge(acc, convertDotNotationToObject(fieldPath, value));
    }, {});

    const documentId = currentSchema.id.reduce(
      (acc, fieldPath) => ({
        ...acc,
        [fieldPath]: context === 'CREATE' ? get(values, fieldPath) : get(docDetails.dataSource, fieldPath),
      }),
      {},
    );

    onSave({
      id: documentId,
      type: values.documentType,
      operation: values.operation,
      data: {
        _id: docDetails?.dataSource?._id,
        ...fieldData,
        header: isHighValue ? values.header : true,
      },
    });

    onHide();
  };

  const isDropdownDisabled = editDisabled || context === 'UPDATE';

  return (
    <Modal opened={opened} onClose={onHide} withCloseButton={false} centered zIndex={9999}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title>{docDetails ? 'Edit document' : 'Add new document'}</Modal.Title>

          <Group justify="space-between" align="center">
            <Switch {...form.getInputProps('header')} checked={form.values.header} label="Header" disabled={isHeaderDisabled()} />
            <Modal.CloseButton />
          </Group>
        </Modal.Header>
        <Stack>
          <Select {...form.getInputProps('documentType')} label="Type of document" data={documentsTypesList.current} disabled={isDropdownDisabled} />

          {form.values.documentType && (
            <DeclarationDocumentFormFactory
              type={form.values.documentType as DeclarationDocumentsEnum}
              form={form}
              editDisabled={editDisabled}
              declarationType={isHighValue ? FormDocumentsDeclarationType.HIGH_VALUES : FormDocumentsDeclarationType.LOW_VALUES}
              isHeader={form.values.header}
              schema={schema}
              declaration={declaration}
            />
          )}
        </Stack>
        {!editDisabled && (
          <Group justify="center" mt="xl">
            <Button data-pw="submit-document-btn" type="submit" variant="filled" size="lg">
              {docDetails ? 'Update' : 'Add'}
            </Button>
          </Group>
        )}
      </form>
    </Modal>
  );
};
